// context/PersonalDataContext.tsx
import { ReactNode, createContext, useContext, useEffect, useState, useCallback } from "react";
import { UserContext } from "features/account";
import { PersonalData, PersonalDataType } from "../types/personalData.types";
import {
  addPersonalDatas as apiAddPersonalDatas,
  getPersonalDatas as apiGetPersonalDatas,
  getPersonalDataById as apiGetPersonalDataById,
  updatePersonalData as apiUpdatePersonalData,
  deletePersonalData as apiDeletePersonalData,
  searchPersonalData as apiSearchPersonalData
} from "../api";

// Grouper les données par type
interface GroupedPersonalData {
  email: PersonalData[];
  phoneNumber: PersonalData[];
  iban: PersonalData[];
  address: PersonalData[];
  socialSecurityNumber: PersonalData[];
  password: PersonalData[];
}

const PersonalDataContext = createContext<{
  personalDatas: PersonalData[];
  grouped: GroupedPersonalData;
  loading: boolean;
  error: string | null;
  addPersonalDatas: (datas: PersonalData[]) => Promise<any>;
  getPersonalDatas: (filterType?: PersonalDataType) => Promise<void>;
  getPersonalDataById: (id: number) => Promise<PersonalData | null>;
  updatePersonalData: (id: number, value: any) => Promise<any>;
  deletePersonalData: (id: number) => Promise<any>;
  searchPersonalData: (type: PersonalDataType, value: string) => Promise<PersonalData[]>;
  refreshPersonalDatas: () => Promise<void>;
  clearError: () => void;
}>({
  personalDatas: [],
  grouped: {
    email: [],
    phoneNumber: [],
    iban: [],
    address: [],
    socialSecurityNumber: [],
    password: []
  },
  loading: false,
  error: null,
  addPersonalDatas: async () => {},
  getPersonalDatas: async () => {},
  getPersonalDataById: async () => null,
  updatePersonalData: async () => {},
  deletePersonalData: async () => {},
  searchPersonalData: async () => [],
  refreshPersonalDatas: async () => {},
  clearError: () => {},
});

const PersonalDataContextProvider = ({ children }: { children: ReactNode }) => {
  const [personalDatas, setPersonalDatas] = useState<PersonalData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { userType, user } = useContext(UserContext);

  // Grouper toutes les données par type
  const grouped: GroupedPersonalData = {
    email: personalDatas.filter(pd => pd.type === 'email'),
    phoneNumber: personalDatas.filter(pd => pd.type === 'phoneNumber'),
    iban: personalDatas.filter(pd => pd.type === 'iban'),
    address: personalDatas.filter(pd => pd.type === 'address'),
    socialSecurityNumber: personalDatas.filter(pd => pd.type === 'socialSecurityNumber'),
    password: personalDatas.filter(pd => pd.type === 'password')
  };

  const clearError = () => setError(null);

  /**
   * Récupérer toutes les données personnelles
   */
  const getPersonalDatas = useCallback(async (filterType?: PersonalDataType) => {
    if (!userType) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiGetPersonalDatas(userType, filterType);

      // La réponse est chiffrée, il faut la déchiffrer
      // Pour l'instant, on suppose que response.encrypt contient les données
      if (response.encrypt) {
        // À déchiffrer avec decryptApiBodyToJson
        console.log('Données reçues chiffrées:', response.encrypt);
        // const decryptedData = decryptApiBodyToJson(response.encrypt);
        // setPersonalDatas(decryptedData.data);
      } else if (response.data) {
        setPersonalDatas(response.data);
      }
    } catch (e: any) {
      console.error('Erreur getPersonalDatas:', e);
      setError(e.message || 'Erreur lors de la récupération des données');
    } finally {
      setLoading(false);
    }
  }, [userType]);

  /**
   * Ajouter plusieurs données personnelles
   */
  const addPersonalDatas = async (datas: PersonalData[]) => {
    if (!userType) throw new Error('User type not defined');

    setLoading(true);
    setError(null);

    try {
      const response = await apiAddPersonalDatas(datas, userType);

      // Rafraîchir la liste après ajout
      await getPersonalDatas();

      return response;
    } catch (e: any) {
      console.error('Erreur addPersonalDatas:', e);
      setError(e.message || 'Erreur lors de l\'ajout des données');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Récupérer une donnée personnelle par ID
   */
  const getPersonalDataById = async (id: number): Promise<PersonalData | null> => {
    if (!userType) throw new Error('User type not defined');

    setLoading(true);
    setError(null);

    try {
      const response = await apiGetPersonalDataById(id, userType);

      if (response.encrypt) {
        // À déchiffrer
        console.log('Donnée reçue chiffrée:', response.encrypt);
        return null;
      }

      return response.data || null;
    } catch (e: any) {
      console.error(`Erreur getPersonalDataById ${id}:`, e);
      setError(e.message || 'Erreur lors de la récupération de la donnée');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Mettre à jour une donnée personnelle
   */
  const updatePersonalData = async (id: number, value: any) => {
    if (!userType) throw new Error('User type not defined');

    setLoading(true);
    setError(null);

    try {
      const response = await apiUpdatePersonalData(id, value, userType);

      // Mettre à jour la donnée dans l'état local
      setPersonalDatas(prev =>
        prev.map(pd => pd.id === id ? { ...pd, value: value.value, isDataBreached: value.isDataBreached, companyWhereDataBreached: value.companyWhereDataBreached } : pd)
      );

      return response;
    } catch (e: any) {
      console.error(`Erreur updatePersonalData ${id}:`, e);
      setError(e.message || 'Erreur lors de la mise à jour');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Supprimer une donnée personnelle
   */
  const deletePersonalData = async (id: number) => {
    if (!userType) throw new Error('User type not defined');

    setLoading(true);
    setError(null);

    try {
      const response = await apiDeletePersonalData(id, userType);

      // Supprimer la donnée de l'état local
      setPersonalDatas(prev => prev.filter(pd => pd.id !== id));

      return response;
    } catch (e: any) {
      console.error(`Erreur deletePersonalData ${id}:`, e);
      setError(e.message || 'Erreur lors de la suppression');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Rechercher des données personnelles
   */
  const searchPersonalData = async (type: PersonalDataType, value: string): Promise<PersonalData[]> => {
    if (!userType) throw new Error('User type not defined');

    setLoading(true);
    setError(null);

    try {
      const response = await apiSearchPersonalData(type, value, userType);

      if (response.encrypt) {
        // À déchiffrer
        console.log('Résultats de recherche chiffrés:', response.encrypt);
        return [];
      }

      return response.data || [];
    } catch (e: any) {
      console.error('Erreur searchPersonalData:', e);
      setError(e.message || 'Erreur lors de la recherche');
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Rafraîchir toutes les données
   */
  const refreshPersonalDatas = useCallback(async () => {
    await getPersonalDatas();
  }, [getPersonalDatas]);

  // Chargement initial
  useEffect(() => {
    if (userType === 'individual' || userType === 'partner') {
      getPersonalDatas();
    }
  }, [userType, getPersonalDatas]);

  const value = {
    personalDatas,
    grouped,
    loading,
    error,
    addPersonalDatas,
    getPersonalDatas,
    getPersonalDataById,
    updatePersonalData,
    deletePersonalData,
    searchPersonalData,
    refreshPersonalDatas,
    clearError
  };

  return (
    <PersonalDataContext.Provider value={value}>
      {children}
    </PersonalDataContext.Provider>
  );
};

export { PersonalDataContext, PersonalDataContextProvider };